import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TopCallItem } from '@/entities/dashboards';
import { appGeneratePath, cn, preventShiftSelection } from '@/shared/lib';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS, fmt } from '../utils';

type Props = {
  call: TopCallItem;
  /** true — выделить красным (для худших звонков) */
  negative?: boolean;
  /** Открытие диалога звонка; `openInNewTab` — пользователь кликнул с shift */
  onOpen: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Ссылка-карточка на диалог из топ-листов лучших/худших звонков.
 * Обычный клик открывает диалог в текущей вкладке, shift+клик — в новой.
 */
export const TopCallLink: FC<Props> = ({ call, negative, onOpen }) => {
  const { t } = useTranslation('qualityDashboard');

  // Диалог адресуем, поэтому карточка остаётся ссылкой — её адрес видно и можно скопировать.
  // Переход выполняем сами: по shift браузер открыл бы окно, а не вкладку.
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onOpen(call.conversationId, event.shiftKey);
  };

  return (
    <a
      href={appGeneratePath('/dialogs/:id', { id: String(call.conversationId) })}
      onMouseDown={preventShiftSelection}
      onClick={handleClick}
      className={cn(
        'block rounded-lg border p-3 no-underline text-inherit',
        DASHBOARD_CLICKABLE_BLOCK_CLASS,
        // Своя заливка нужна и обычной карточке: без неё приглушать при наведении нечего.
        negative ? 'border-red-200 bg-red-50' : 'bg-card',
      )}
    >
      <div className="text-sm">
        <strong>{call.consultantName}</strong> — {t('topCalls.score')}: <strong>{fmt(call.totalRating)}</strong>
      </div>
      <div className="text-xs text-muted-foreground">
        {t('topCalls.date')}: {call.evaluationDate} | {t('topCalls.client')}: {call.clientName} |{' '}
        {t('topCalls.externalId')}: {call.externalId}
      </div>
    </a>
  );
};
